import React, { useState, useEffect } from "react";
import { X, Check, Loader2 } from "lucide-react";
import type { ClaimedCoupon } from "../../api/couponApi";
import "../../styles/coupon-page.css";

interface CouponModalProps {
  coupon: ClaimedCoupon | null;
  isOpen: boolean;
  onClose: () => void;
  onRedeem: (code: string) => void;
  redeeming: boolean;
}

function calculateTimeLeft(expiresAtStr?: string) {
  if (!expiresAtStr) return { hours: "00", minutes: "00", seconds: "00", totalSec: 0 };
  const expiryTime = new Date(expiresAtStr).getTime();
  const now = new Date().getTime();
  const diffSec = Math.max(0, Math.floor((expiryTime - now) / 1000));

  const h = Math.floor(diffSec / 3600);
  const m = Math.floor((diffSec % 3600) / 60);
  const s = diffSec % 60;

  return {
    hours: String(h).padStart(2, "0"),
    minutes: String(m).padStart(2, "0"),
    seconds: String(s).padStart(2, "0"),
    totalSec: diffSec,
  };
}

export default function CouponModal({
  coupon,
  isOpen,
  onClose,
  onRedeem,
  redeeming,
}: CouponModalProps) {
  const [timeLeft, setTimeLeft] = useState(() =>
    calculateTimeLeft(coupon?.expires_at)
  );

  useEffect(() => {
    if (!coupon?.expires_at) return;

    setTimeLeft(calculateTimeLeft(coupon.expires_at));
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft(coupon.expires_at));
    }, 1000);

    return () => clearInterval(interval);
  }, [coupon?.expires_at]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !coupon) return null;

  const isRedeemed = coupon.status === "redeemed";
  const isExpired = timeLeft.totalSec <= 0 && !isRedeemed;

  return (
    <div
      className="coupon-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="coupon-modal-wrap" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          type="button"
          className="coupon-modal-close-btn"
          onClick={onClose}
          aria-label="Close coupon modal"
        >
          <X size={16} />
        </button>

        {/* Ticket Box */}
        <div className={`coupon-popup-ticket ${isExpired ? "is-expired" : ""}`}>
          {/* Top Half */}
          <div className="coupon-popup-top-section">
            <h2 className={`coupon-popup-discount ${isExpired ? "is-expired" : ""}`}>
              {coupon.discount_title}
            </h2>
            <span className={`coupon-popup-duration ${isExpired ? "is-expired" : ""}`}>
              {coupon.duration_text}
            </span>

            {/* Code Box */}
            <div className={`coupon-popup-code-box ${isExpired ? "is-expired" : ""}`}>
              <span className="coupon-popup-code-text">{coupon.coupon_code}</span>

              {isRedeemed ? (
                <button
                  type="button"
                  className="coupon-redeem-btn is-redeemed"
                  disabled
                >
                  <Check size={13} />
                  <span>Redeemed</span>
                </button>
              ) : isExpired ? (
                <button
                  type="button"
                  className="coupon-redeem-btn"
                  disabled
                  style={{ background: "#334155", color: "#94a3b8" }}
                >
                  Expired
                </button>
              ) : (
                <button
                  type="button"
                  className="coupon-redeem-btn"
                  onClick={() => onRedeem(coupon.coupon_code)}
                  disabled={redeeming}
                >
                  {redeeming ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    "Redeem"
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Perforated border with side notches */}
          <div className="coupon-popup-perforation">
            <div className="coupon-popup-notch-left" />
            <div className="coupon-popup-notch-right" />
          </div>

          {/* Bottom Countdown Section */}
          <div className="coupon-popup-timer-section">
            <span className={`coupon-timer-label ${isExpired ? "is-expired" : ""}`}>
              {isExpired ? "EXPIRED" : "EXPIRES IN"}
            </span>

            <div className="coupon-timer-boxes" role="timer" aria-label="Coupon expiration countdown">
              <div className="coupon-time-digit-box">{isExpired ? "00" : timeLeft.hours}</div>
              <span className="coupon-time-colon">:</span>
              <div className="coupon-time-digit-box">{isExpired ? "00" : timeLeft.minutes}</div>
              <span className="coupon-time-colon">:</span>
              <div className="coupon-time-digit-box">{isExpired ? "00" : timeLeft.seconds}</div>
            </div>
          </div>
        </div>

        {/* Conditions Card Below Ticket */}
        {coupon.conditions && coupon.conditions.length > 0 && (
          <div className="coupon-popup-conditions-card">
            <h3 className="coupon-conditions-title">Conditions</h3>
            <ul className="coupon-conditions-list">
              {coupon.conditions.map((condition, idx) => (
                <li key={idx}>{condition}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
